import {
  AddIcon,
  AlertNotification,
  ArrowIcon,
  Button,
  colors,
  H2,
  H3,
  IconButton,
  Popup,
  PopupHeader,
  RadioIcon,
  SkubotSpinner,
  Table,
  TBody,
  TD,
  Text,
  TH,
  THead,
  TR,
  XIcon,
} from "@commonsku/styles";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useContainerScroll } from "../../../../hooks";
import { BASE_ZINDEX } from "../../../../popup-factory";
import styled from "styled-components";
import { ScrollContainer } from "../../../../hooks/useContainerScroll";
import { MapErrorProductType } from "../CheckoutsErrorPopup/CheckoutsErrorPopup";
import { ItemVariant } from "../../../../interfaces/ItemVariant";
import { getImageSrc, oauth } from "../../../../utils";
import { getAllShopVariants } from "../../../../actions/shop";
import { useNavigate } from "react-router";
import MapCheckoutProductsPopupBar from "./MapCheckoutProductsPopupBar";
import { useSelectedProductData } from "../../helpers";
import {
  selectIsLoading,
  setIsLoadingFalse,
  setIsLoadingTrue,
} from "../../../../redux/isLoading";

const ContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`;

const BodyContainer = styled.div`
  width: 100%;
  max-height: 465px !important;
  overflow: hidden;
  flex: 1;
  position: relative;
  margin-top: 24px !important;
  margin-bottom: 24px !important;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

interface MapCheckourProductsPopupProps {
  product: MapErrorProductType;
  shop_id: string;
  order_id: string;
  handledLinkedItems: (productId: string) => void;
  shop_number: number;
  onClose?: () => void;
}

const MapCheckoutProductsPopup = ({
  product,
  shop_id,
  order_id,
  handledLinkedItems,
  shop_number,
  onClose,
}: MapCheckourProductsPopupProps) => {
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsLoading);
  const onPopupClose = (index) => {
    onClose?.();
  };
  const items = useSelector((state: any) => state?.entities?.items);
  const { canScrollDown, scrollDown } = useContainerScroll(tableRef);
  const [selectedProduct, setSelectedProduct] = useState<ItemVariant | null>(
    null
  );

  const allVariants = useSelector(
    (state: any) => state?.entities?.shops[shop_id]?.variants
  );

  const handleLinkProduct = () => {
    if (!selectedProduct) {
      console.error("No product selected");
      return;
    }
    dispatch(setIsLoadingTrue());
    const data = {
      shop_id,
      variant_mapping: [
        {
          shopify_variant_id: product.external_variant_id,
          item_variant_id: selectedProduct.item_variant_id,
          checkout_item_ids: product.checkoutItemIds,
          external_sku_id: product.sku || "",
        },
      ],
    };
    oauth("POST", "checkout/map-shopify-variants", data)
      .then(() => {
        handledLinkedItems(product.external_variant_id);
        onPopupClose(1);
        dispatch(setIsLoadingFalse());
        dispatch(getAllShopVariants(shop_id, order_id));
      })
      .catch((err) => {
        dispatch(setIsLoadingFalse());
        console.error("Error linking product:", err);
      });
  };

  const { selectedProductImgSrc, selectedProductItemName } =
    useSelectedProductData(selectedProduct, items);

  return (
    <Popup
      overlayZIndex={BASE_ZINDEX + 101}
      zIndex={BASE_ZINDEX + 201}
      style={{
        maxHeight: 793,
        borderRadius: 10,
      }}
      header={
        <PopupHeader
          style={{
            padding: 0,
            alignItems: "center",
            marginTop: 16,
            marginBottom: 16,
          }}
        >
          <H2
            style={{ margin: "0px", fontSize: 24, fontWeight: 600 }}
            className="title"
          >
            Map Checkout Product to commonsku Product / Variant
          </H2>
          <XIcon
            style={{ cursor: "pointer" }}
            size="large"
            onClick={() => onPopupClose(1)}
            disabled={isLoading}
          />
        </PopupHeader>
      }
    >
      <ContentDiv>
        {selectedProduct ? (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginTop: 8,
            }}
          >
            <MapCheckoutProductsPopupBar
              product={product}
              isExternal
              selectedProduct={selectedProduct}
            />
            <div style={{ marginLeft: 12, marginRight: 12 }}>
              <ArrowIcon direction="right" size="large" />
            </div>
            <MapCheckoutProductsPopupBar
              selectedProduct={selectedProduct}
              selectedProductimgSrc={selectedProductImgSrc}
              selectedProductItemName={selectedProductItemName}
            />
          </div>
        ) : (
          <MapCheckoutProductsPopupBar product={product} />
        )}
        {allVariants?.length > 0 &&
        allVariants.some((variant: ItemVariant) => !variant.locked) ? (
          <>
            <BodyContainer>
              <div style={{ height: "100%", overflowY: "auto" }} ref={tableRef}>
                <Table>
                  <THead
                    style={{
                      height: "56px",
                      width: "100%",
                    }}
                  >
                    <TR
                      style={{
                        backgroundColor: `rgb(218, 233, 238)`,
                      }}
                    >
                      <TH sticky></TH>
                      <TH sticky>Product</TH>
                      <TH sticky>Color</TH>
                      <TH sticky>Size</TH>
                      <TH sticky>External SKU</TH>
                      <TH sticky></TH>
                    </TR>
                  </THead>
                  <TBody>
                    {allVariants
                      .filter((variant: ItemVariant) => !variant.locked)
                      .map((variant: ItemVariant) => (
                        <TR
                          key={variant.item_variant_id}
                          style={{
                            backgroundColor:
                              selectedProduct?.item_variant_id ===
                              variant.item_variant_id
                                ? colors.neutrals.bg1
                                : "",
                          }}
                          onClick={() => setSelectedProduct(variant)}
                        >
                          <TD style={{ minWidth: 48, textAlign: "center" }}>
                            <RadioIcon
                              type="checkbox"
                              name="item-selected-checkbox"
                              style={{ margin: "auto" }}
                              selected={
                                selectedProduct?.item_variant_id ===
                                variant.item_variant_id
                              }
                            />
                          </TD>
                          <TD style={{ minWidth: 280 }}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              <img
                                src={
                                  items?.[variant.item_id]?.files.map((file) =>
                                    getImageSrc(file, "small")
                                  )[0] || ""
                                }
                                style={{
                                  height: 45,
                                  width: 45,
                                  display: "inline-block",
                                }}
                                alt={items?.[variant.item_id]?.item_name}
                              />
                              <Text
                                as="span"
                                style={{
                                  display: "inline-block",
                                  fontSize: 14,
                                  fontWeight: 400,
                                  color: `${colors.primary1["70"]}`,
                                  marginLeft: 4,
                                }}
                              >
                                {items?.[variant.item_id]?.item_name}
                              </Text>
                            </div>
                          </TD>
                          <TD style={{ minWidth: 112 }}>
                            {variant?.product_sku
                              ? JSON.parse(variant.product_sku.sku_options)
                                  .color
                              : variant.color?.color_name}
                          </TD>
                          <TD style={{ minWidth: 112 }}>
                            {variant?.product_sku
                              ? JSON.parse(variant.product_sku.sku_options).size
                              : variant.size?.size_name}
                          </TD>
                          <TD style={{ width: "100%" }}>
                            {variant?.external_id || ""}
                          </TD>
                        </TR>
                      ))}
                  </TBody>
                </Table>
              </div>
              <ScrollContainer
                bottom={-21}
                canScroll={canScrollDown()}
                color={colors.primary1["70"]}
                isUp={false}
                left
                onClick={scrollDown}
                right
                top
                width="100%"
                style={{
                  position: "absolute",
                  cursor: "pointer",
                }}
                margin={0}
                arrowSize="large"
                padding={10}
              />
            </BodyContainer>
            <div
              style={{
                flexShrink: 0,
                display: "flex",
                marginBottom: 16,
              }}
            >
              <AlertNotification alertType="neutral">
                This will replace the SKU received from Shopify with the
                selected commonsku external SKU. Correct your mapping of
                products to prevent future errors.
              </AlertNotification>
              <Button
                onClick={handleLinkProduct}
                size="large"
                style={{ marginLeft: "auto" }}
                disabled={isLoading || !selectedProduct}
              >
                {isLoading ? (
                  <SkubotSpinner
                    size="button"
                    skubot={false}
                    style={{ margin: "auto" }}
                  />
                ) : (
                  "Link Product"
                )}
              </Button>
            </div>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <H3
              style={{
                fontWeight: 600,
                fontSize: 20,
                color: colors.neutrals[90],
                marginBottom: 24,
              }}
            >
              You don't have any products / variants to be mapped
            </H3>
            <Text
              as="p"
              style={{
                maxWidth: 648,
                maxHeight: 120,
                textAlign: "center",
                fontWeight: 400,
                fontSize: 16,
                color: colors.neutrals[90],
                marginBottom: 24,
              }}
            >
              To map your Shopify items, you'll first need to add Products and
              their color/size Variants in commonsku. Once your Products are set
              up, you’ll be able to match incoming Shopify orders to your
              internal items. Once mapped, the commonsku external SKUs will
              replace the Shopify variant SKUs.
            </Text>
            <IconButton
              iconPosition="left"
              Icon={<AddIcon />}
              onClick={() => {
                onPopupClose(2);
                navigate(`/shop/${shop_number}/products`);
              }}
            >
              Add Products / Variants
            </IconButton>
          </div>
        )}
      </ContentDiv>
    </Popup>
  );
};

export default MapCheckoutProductsPopup;
